import { z } from 'zod';

// ────────────────────────────────────────────────────────────────────────────
// Pagination helpers (inlined — no external common schema needed)
// ────────────────────────────────────────────────────────────────────────────

const PaginationSchema = z.object({
  lastKey: z.string().optional().nullable(),
  limit: z.number().int().min(1).max(1000).default(200),
  nextToken: z.string().optional().nullable(),
});

const PaginatedResponseSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    count: z.number().int().min(0),
    items: z.array(itemSchema),
    lastKey: z.string().optional().nullable(),
    nextToken: z.string().optional().nullable(),
  });

// ────────────────────────────────────────────────────────────────────────────
// Rail Summary
// ────────────────────────────────────────────────────────────────────────────

export const RailSummaryStatusSchema = z.enum([
  'En Route',
  'At Customer',
  'Complete',
]);

export type IRailSummaryStatus = z.infer<typeof RailSummaryStatusSchema>;

export const RailSummarySchema = z.object({
  arrivalDate: z.string().nullable(),
  bolNumber: z.string().nullable(),
  currentRailroad: z.string().nullable(),
  customerName: z.string(),
  daysAtDestination: z.number().nullable(),
  daysOver: z.number().nullable(),
  destination: z.string().nullable(),
  eta: z.string().nullable(),
  freeDays: z.number().nullable(),
  id: z.string(),
  kochParty: z.string().nullable(),
  lastSighted: z.string().nullable(),
  lastSightedLocation: z.string().nullable(),
  origin: z.string().nullable(),
  product: z.string().nullable(),
  releasedDate: z.string().nullable(),
  releaseNumber: z.string().nullable(),
  shipDate: z.string().nullable(),
  status: RailSummaryStatusSchema,
  unitNumber: z.string(),
});

export type IRailSummary = z.infer<typeof RailSummarySchema>;

export const RailSummaryListQuerySchema = PaginationSchema.extend({
  bolNumber: z.string().optional(),
  customerName: z.string().optional(),
  destination: z.string().optional(),
  kochParty: z.string().optional(),
  origin: z.string().optional(),
  product: z.string().optional(),
  releaseNumber: z.string().optional(),
  status: RailSummaryStatusSchema.optional(),
  unitNumber: z.string().optional(),
});

export type IRailSummaryListQuery = z.infer<typeof RailSummaryListQuerySchema>;

export const RailSummaryListResponseSchema =
  PaginatedResponseSchema(RailSummarySchema);

export type IRailSummaryListResponse = z.infer<
  typeof RailSummaryListResponseSchema
>;
